import { create } from 'zustand';
import { supabase } from '../supabase';
import { friendlyDbError } from '../utils/friendlyDbError';
import type { LeadFormDocumentRequirement } from '../types/leadForm';

/**
 * Modelli di elenco per le richieste di documenti.
 *
 * Chi chiede documenti chiede quasi sempre gli stessi: il modello si scrive
 * una volta e poi si sceglie con un clic, sia quando si apre una richiesta a
 * mano sia nel campo "Documenti richiesti" di un modulo. La richiesta copia le
 * voci al momento della creazione: modificare un modello non cambia le
 * richieste gia' mandate.
 */

export interface ModelloDocumenti {
  id: string;
  name: string;
  requirements: LeadFormDocumentRequirement[];
  giorni_validita: number;
}

interface Stato {
  modelli: ModelloDocumenti[];
  caricati: boolean;
  errore: string | null;
  carica: () => Promise<void>;
  salva: (modello: Omit<ModelloDocumenti, 'id'> & { id?: string }) => Promise<ModelloDocumenti | null>;
  elimina: (id: string) => Promise<void>;
}

/** Voci pulite: senza righe vuote e con un id stabile per ciascuna. */
export function vociPulite(voci: LeadFormDocumentRequirement[]): LeadFormDocumentRequirement[] {
  return voci
    .filter((v) => v.label.trim().length > 0)
    .map((v) => ({ ...v, id: v.id || crypto.randomUUID(), label: v.label.trim() }));
}

/**
 * Copia le voci di un modello dando a ciascuna un id nuovo: le voci di due
 * richieste diverse non devono condividere l'id, altrimenti un documento
 * potrebbe sembrare consegnato su una richiesta mentre e' dell'altra.
 */
export function copiaVoci(voci: LeadFormDocumentRequirement[]): LeadFormDocumentRequirement[] {
  return voci.map((v) => ({ ...v, id: crypto.randomUUID() }));
}

export const useDocumentRequestTemplateStore = create<Stato>((set, get) => ({
  modelli: [],
  caricati: false,
  errore: null,

  carica: async () => {
    const { data, error } = await supabase
      .from('document_request_templates')
      .select('id, name, requirements, giorni_validita')
      .order('name');
    if (error) {
      set({ errore: friendlyDbError(error), caricati: true });
      return;
    }
    set({ modelli: (data ?? []) as ModelloDocumenti[], caricati: true, errore: null });
  },

  salva: async (modello) => {
    try {
      const voci = vociPulite(modello.requirements);
      if (!modello.name.trim() || voci.length === 0) {
        throw new Error('Serve un nome e almeno un documento.');
      }

      if (modello.id) {
        const { data, error } = await supabase
          .from('document_request_templates')
          .update({ name: modello.name.trim(), requirements: voci, giorni_validita: modello.giorni_validita })
          .eq('id', modello.id)
          .select('id, name, requirements, giorni_validita')
          .single();
        if (error) throw error;
        set({ modelli: get().modelli.map((m) => (m.id === modello.id ? (data as ModelloDocumenti) : m)) });
        return data as ModelloDocumenti;
      }

      const { data: utente } = await supabase.auth.getUser();
      const { data: profilo } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', utente.user?.id ?? '')
        .single();
      if (!profilo?.organization_id) throw new Error('Utente senza organizzazione');

      const { data, error } = await supabase
        .from('document_request_templates')
        .insert({
          organization_id: profilo.organization_id,
          name: modello.name.trim(),
          requirements: voci,
          giorni_validita: modello.giorni_validita,
          created_by: utente.user?.id ?? null,
        })
        .select('id, name, requirements, giorni_validita')
        .single();
      if (error) throw error;

      const nuovi = [...get().modelli, data as ModelloDocumenti].sort((a, b) =>
        a.name.localeCompare(b.name, 'it'),
      );
      set({ modelli: nuovi });
      return data as ModelloDocumenti;
    } catch (error) {
      set({ errore: error instanceof Error ? error.message : friendlyDbError(error) });
      return null;
    }
  },

  elimina: async (id) => {
    const { error } = await supabase.from('document_request_templates').delete().eq('id', id);
    if (error) {
      set({ errore: friendlyDbError(error) });
      return;
    }
    set({ modelli: get().modelli.filter((m) => m.id !== id) });
  },
}));
